import { useCallback, useEffect, useState } from "react";
import {
  getSidebarController,
  SIDEBAR_STATE_CHANGE_EVENT,
  type SidebarController,
} from "../lib/sidebar-controller";

interface ShellSidebarSnapshot {
  controller?: SidebarController;
  isOpen: boolean;
}

const EMPTY_SIDEBAR_SNAPSHOT: ShellSidebarSnapshot = {
  controller: undefined,
  isOpen: false,
};

const readShellSidebarSnapshot = (): ShellSidebarSnapshot => {
  const controller = getSidebarController();

  if (!controller) {
    return EMPTY_SIDEBAR_SNAPSHOT;
  }

  return {
    controller,
    isOpen: controller.isOpen(),
  };
};

export const useShellSidebar = (enabled: boolean) => {
  const [snapshot, setSnapshot] = useState<ShellSidebarSnapshot>(
    enabled ? readShellSidebarSnapshot : EMPTY_SIDEBAR_SNAPSHOT,
  );

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const syncSnapshot = () => {
      setSnapshot(enabled ? readShellSidebarSnapshot() : EMPTY_SIDEBAR_SNAPSHOT);
    };

    syncSnapshot();

    if (!enabled) {
      return;
    }

    window.addEventListener(SIDEBAR_STATE_CHANGE_EVENT, syncSnapshot);

    return () => {
      window.removeEventListener(SIDEBAR_STATE_CHANGE_EVENT, syncSnapshot);
    };
  }, [enabled]);

  const toggle = useCallback(() => {
    snapshot.controller?.toggle();
  }, [snapshot.controller]);

  return {
    controller: snapshot.controller,
    isOpen: snapshot.isOpen,
    isAvailable: Boolean(snapshot.controller),
    toggle,
  };
};
